import styles from './statistics-styles.module.scss';

import { Theme, useMediaQuery } from '@mui/material';
import { ColumnDef, useReactTable } from '@tanstack/react-table';
import { getSortedRowModel } from '@tanstack/table-core';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CostCenter } from '@work4all/models/lib/Classes/CostCenter.entity';
import { StatisticSalesVoumeByCostCenter } from '@work4all/models/lib/Classes/StatisticSalesVoumeByCostCenter.entity';
import { StatisticsSalesVolumeByCostCenterColumn } from '@work4all/models/lib/Classes/StatisticsSalesVolumeByCostCenterColumn.entity';
import { StatisticsSalesVolumeByCostCenterRow } from '@work4all/models/lib/Classes/StatisticsSalesVolumeByCostCenterRow.entity';

import { ICssClasses } from '../types';
import { CurrencyCell } from '../utils';
import { useDefaultReactTableOptions } from '../utils/use-default-react-table-options';
import { VirtualizedTableV8 } from '../VirtualizedTableV8';

import { getStatisticFormatedDate } from './getStatisticFormatedDate';

interface StatisticSalesVolumeByCostCenterTableProps {
  data: StatisticSalesVoumeByCostCenter;
  tableClasses?: ICssClasses;
}

interface MonthDefinition {
  amount: number;
  date: string;
}

interface StatisticItemRemodeled {
  costCenter: CostCenter;
  sum: number;
  months: MonthDefinition[];
}

const GetTotalSum = (
  months: StatisticsSalesVolumeByCostCenterColumn[],
  rowIndex: number
) => {
  let result = 0;
  months.forEach((column) => {
    result += column.rows?.[rowIndex].amount;
  });

  return result;
};

const GetTotalMonthValue = (data: StatisticItemRemodeled[], month: number) => {
  let result = 0;

  data.forEach((row) => {
    result += row.months[month].amount;
  });

  return (
    <CurrencyCell
      className={clsx({ [styles.red]: result < 0 })}
      value={result}
      maximumFractionDigits={0}
      minimumFractionDigits={0}
      hideUnit={false}
    />
  );
};

const GetTotalTotalSumValue = (data: StatisticItemRemodeled[]) => {
  let result = 0;

  data.forEach((row) => {
    result += row?.sum || 0;
  });

  return (
    <CurrencyCell
      className={clsx({ [styles.red]: result < 0 })}
      value={result}
      maximumFractionDigits={0}
      minimumFractionDigits={0}
      hideUnit={false}
    />
  );
};

export const StatisticSalesVolumeByCostCenterTable: React.FC<
  StatisticSalesVolumeByCostCenterTableProps
> = (props) => {
  const { data, tableClasses } = props;

  const isViewportDownMd = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md')
  );

  const { t } = useTranslation();

  const newData: StatisticItemRemodeled[] = useMemo(() => {
    let result: StatisticItemRemodeled[] = [];

    if (data.columns === undefined) {
      return [];
    }
    const months = [...data.columns].sort((a, b) => {
      if (new Date(a.rangeStart) > new Date(b.rangeStart)) {
        return 1;
      }
      if (new Date(a.rangeStart) < new Date(b.rangeStart)) {
        return -1;
      }
      return 0;
    });

    const unknonwCostCenters: StatisticItemRemodeled[] = [];
    const knonwCostCenters: StatisticItemRemodeled[] = [];
    months?.[0].rows.forEach(
      (row: StatisticsSalesVolumeByCostCenterRow, rowIndex) => {
        if (row.costCenter === null) {
          const unknownCostCenter: StatisticItemRemodeled = {
            costCenter: row.costCenter,
            sum: GetTotalSum(months, rowIndex),
            months: months.map((col) => {
              return {
                amount: col.rows?.[rowIndex].amount,
                date: col.rows?.[rowIndex].date,
              };
            }),
          };
          unknonwCostCenters.push(unknownCostCenter);
        }
        if (row.costCenter !== null) {
          const knownCostCenter: StatisticItemRemodeled = {
            costCenter: row.costCenter,
            sum: GetTotalSum(months, rowIndex),
            months: months.map((col) => {
              return {
                amount: col.rows?.[rowIndex].amount,
                date: col.rows?.[rowIndex].date,
              };
            }),
          };
          knonwCostCenters.push(knownCostCenter);
        }
      }
    );

    if (months?.[0]?.rows.length === 0) {
      knonwCostCenters.push({
        costCenter: null,
        months: months.map((col) => {
          return {
            amount: 0,
            date: col.rangeStart,
          };
        }),
        sum: 0,
      });
    }

    const unknownCostCentersReduce: StatisticItemRemodeled[] = [];
    unknownCostCentersReduce.push(unknonwCostCenters[0]);
    unknonwCostCenters.slice(1).forEach((row: StatisticItemRemodeled) => {
      row.months.forEach((month: MonthDefinition, colIndex) => {
        unknownCostCentersReduce[0].months[colIndex].amount += month.amount;
      });
    });

    if (unknownCostCentersReduce[0]) {
      knonwCostCenters.push(unknownCostCentersReduce[0]);
    }
    result = knonwCostCenters;

    result = result.sort((a, b) => {
      if (a.sum > b.sum) {
        return -1;
      }
      if (a.sum < b.sum) {
        return 1;
      }
      return 0;
    });

    return result;
  }, [data]);

  const columns = useMemo<ColumnDef<StatisticItemRemodeled, any>[]>(() => {
    const defaultProperties: Partial<ColumnDef<StatisticItemRemodeled, any>> = {
      size: isViewportDownMd ? 100 : 150,
      cell: (props: { getValue: () => number }) => {
        const value = props.getValue();
        if (value === 0) {
          return '';
        }
        return (
          <CurrencyCell
            className={clsx({ [styles.red]: value < 0 })}
            value={value}
            maximumFractionDigits={0}
            minimumFractionDigits={0}
            hideUnit={true}
          />
        );
      },
      alignment: 'right',
    };

    let result: ColumnDef<StatisticItemRemodeled, any>[] = [
      {
        header: t('COMMON.COST_CENTER'),
        accessorKey: 'costCenter.name',
        size: isViewportDownMd ? 150 : 300,
        cell: (props: { getValue: () => string }) => {
          const value = props.getValue();
          if (value === undefined) {
            return 'ohne Kostenstelle';
          }
          return <span>{value}</span>;
        },
      },

      {
        ...defaultProperties,
        header: t('COMMON.SUM'),
        accessorKey: 'sum',
        footer: () => GetTotalTotalSumValue(newData),
        cell: (props: { getValue: () => number }) => {
          const value = props.getValue();
          if (value === 0) {
            return '';
          }
          return (
            <CurrencyCell
              className={clsx({ [styles.red]: value < 0 })}
              value={value}
              maximumFractionDigits={0}
              minimumFractionDigits={0}
              hideUnit={true}
            />
          );
        },
      },
    ];

    if (newData[0] !== undefined) {
      const monthColumns = newData?.[0].months.map((month, monthIndex) => {
        return {
          ...defaultProperties,
          header: getStatisticFormatedDate(new Date(month.date), 'MMM yyyy'),
          accessorFn: (row: StatisticItemRemodeled) =>
            row.months[monthIndex]?.amount,
          id: `months_${monthIndex}_amount`,
          footer: () => GetTotalMonthValue(newData, monthIndex),
        } as ColumnDef<StatisticItemRemodeled, number>;
      });

      result = [...result, ...monthColumns];
    }

    return result;
  }, [newData, t, isViewportDownMd]);

  const table = useReactTable<StatisticItemRemodeled>({
    ...useDefaultReactTableOptions(),
    getSortedRowModel: getSortedRowModel(),
    columns,
    data: newData,
    enableMultiRowSelection: false,
    enableColumnResizing: false,
    entityType: null,
    initialState: {
      columnPinning: { left: ['costCenter.name'] },
    },
    getSubRows: () => [] as StatisticItemRemodeled[],
  });

  return (
    <VirtualizedTableV8
      table={table}
      fetchMore={() => null}
      classes={tableClasses}
      className={styles.table}
      displayFooter
    />
  );
};
